import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  TextField, Box, Button, Tooltip, IconButton,
  Typography, 
  Paper, InputAdornment, Grid, 
  Card, CardContent, CardHeader
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import DeleteIcon from '@mui/icons-material/Delete';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import { useTranslation } from 'react-i18next';
import TagsDropdown from '../TagsDropdown';
import ActionSettingsLayout from '../ActionSettingsHeader';
import { useActionSettings } from '../../hooks/useActionSettings';
import TagEditor from '../Widgets/TagEditor';
import { useFolderRestrictions } from '../useFolderRestrictions';
import { useFolderPicker } from '../useFolderPicker';

const ActionsGenerativeAnswer = ({
  caseid,
  tags,
  settingId,
  onSaveStatusChange,
  actionType = 'generativeanswer',
  actionTitle
}) => {
  const { t } = useTranslation();
  const [context, setContext] = useState('');
  const [filePath, setFilePath] = useState('');
  const [filePathError, setFilePathError] = useState('');
  const [fileBrowseDisabled, setFileBrowseDisabled] = useState(false);
  const [tagPrompts, setTagPrompts] = useState([
    { id: Date.now(), tag: '', prompt: '' }
  ]);
  const [genAnchorEl, setGenAnchorEl] = useState(null);
  const [genMenuType, setGenMenuType] = useState('');
  const [activeTagIndex, setActiveTagIndex] = useState(null);

  const contextRef = useRef(null);
  const promptRefs = useRef({});
  const { restrictToUserFolder, allowedRoots, displayRoot } = useFolderRestrictions(caseid);
  const allowedRootsText = restrictToUserFolder
    ? (allowedRoots?.length ? allowedRoots.join(', ') : (displayRoot || t('generative_answer.file_path_root_label', 'your restricted root')))
    : '';
  const filePathHelperText = filePathError
    ? filePathError
    : (restrictToUserFolder
        ? t('generative_answer.file_path_help', 'Paths must stay within allowed folders: {allowed}', { allowed: allowedRootsText })
        : t('generative_answer.file_path_help_any', 'Paths must be accessible to the server.')
      );
  const { handleBrowseDirectory: openFileBrowser, FolderBrowserComponent: FileBrowserComponent } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    allowFileSelection: true,
    onFolderSelected: (selectedPath) => {
      if (selectedPath) {
        setFilePath(selectedPath);
        if (filePathError) {
          setFilePathError('');
        }
      }
      setFileBrowseDisabled(false);
    },
    onFolderBrowserClose: () => {
      setFileBrowseDisabled(false);
    }
  });
  const handleFileBrowse = useCallback(() => {
    setFileBrowseDisabled(true);
    openFileBrowser();
  }, [openFileBrowser]);

  // Unified action settings hook
  const resolvedActionTitle = actionTitle || t('workflow.generativeanswer', 'Generative Answer');

  const {
    isEnabled,
    setIsEnabled,
    conditions,
    handleConditionsChange,
    isSaveDisabled,
    forceRunLoading,
    forceRunResult,
    popUpOpen,
    forceRunWarningOpen,
    forceRunWarningCount,
    handleSave,
    handleForceRun,
    handleForceRunConfirm,
    handleForceRunCancel,
    handleCloseStatusWindow,
    updateSaveDisabledState
  } = useActionSettings({
    settingId,
    caseid,
    actionType,
    onSaveStatusChange,
    customComparison: (initialData) => {
      // Compare current state with initial settings
      if (!initialData) return false;
      
      const currentTagPrompts = tagPrompts.map(tp => ({
        id: tp.id,
        tag: tp.tag,
        prompt: tp.prompt
      }));
      
      // Compare with initial data
      const initialContext = initialData.context || '';
      const initialTagPrompts = initialData.tagPrompts || [];
      const initialFilePath = initialData.filePath || '';
      
      const contextUnchanged = context === initialContext;
      const tagPromptsUnchanged = JSON.stringify(currentTagPrompts) === JSON.stringify(initialTagPrompts);
      const filePathUnchanged = actionType !== 'generativeanswerfile' || filePath === initialFilePath;
      
      return contextUnchanged && tagPromptsUnchanged && filePathUnchanged;
    },
    customSaveData: () => ({
      context,
      tagPrompts: tagPrompts.map(tp => ({
        id: tp.id,
        tag: tp.tag,
        prompt: tp.prompt
      })),
      ...(actionType === 'generativeanswerfile' ? { filePath: filePath.trim() } : {}),
      useConditions: true,
      conditions
    }),
    loadDataFunction: (data) => {
      console.log('Loading generative answer data:', data);
      
      // Parse the data field if it's a JSON string
      let parsedData = {};
      if (data.data && typeof data.data === 'string') {
        try {
          parsedData = JSON.parse(data.data);
        } catch (e) {
          console.error('Failed to parse data JSON:', e);
          return;
        }
      } else if (data.customData) {
        parsedData = data.customData;
      }
      
      console.log('Parsed data:', parsedData);
      
      // Handle context
      if (parsedData.context !== undefined) {
        setContext(parsedData.context);
      }
      
      if (parsedData.filePath !== undefined) {
        setFilePath(parsedData.filePath);
      } else {
        setFilePath('');
      }
      
      // Handle tagPrompts
      if (parsedData.tagPrompts && Array.isArray(parsedData.tagPrompts)) {
        const loadedTagPrompts = parsedData.tagPrompts.map((tp, index) => ({
          id: tp.id || Date.now() + index,
          tag: tp.tag || '',
          prompt: tp.prompt || ''
        }));
        setTagPrompts(loadedTagPrompts);
      } else {
        // Legacy support for old single tag/instructions format
        const legacyTagPrompts = [{
          id: Date.now(),
          tag: parsedData.tag || '',
          prompt: parsedData.instructions || ''
        }];
        setTagPrompts(legacyTagPrompts);
      }
    }
  });

  // Update save state when context or tagPrompts change
  useEffect(() => {
    if (updateSaveDisabledState) {
      updateSaveDisabledState();
    }
  }, [context, tagPrompts, filePath, updateSaveDisabledState]);

  const insertTokenToEditor = useCallback((tagValue, targetType, tagIndex = null) => {
    if (targetType === 'context') {
      contextRef.current?.insertTag(tagValue);
    } else if (targetType === 'prompt' && tagIndex !== null) {
      const promptId = tagPrompts[tagIndex]?.id;
      if (promptId) {
        promptRefs.current[promptId]?.insertTag(tagValue);
      }
    } else if (targetType === 'filePath') {
      const token = `{${tagValue}}`;
      setFilePath((prev) => prev + token);
    }
  }, [tagPrompts, setFilePath]);

  // Helper functions for managing tag prompts
  const addTagPrompt = () => {
    const newTagPrompt = {
      id: Date.now(),
      tag: '',
      prompt: ''
    };
    setTagPrompts([...tagPrompts, newTagPrompt]);
  };

  const removeTagPrompt = (index) => {
    if (tagPrompts.length > 1) {
      const promptIdToRemove = tagPrompts[index].id;
      if (promptRefs.current[promptIdToRemove]) {
        delete promptRefs.current[promptIdToRemove];
      }
      const updated = tagPrompts.filter((_, i) => i !== index);
      setTagPrompts(updated);
    }
  };

  const updateTagPromptTag = (index, newTag) => {
    const updated = [...tagPrompts];
    updated[index].tag = newTag;
    setTagPrompts(updated);
  };

  const updateTagPromptPrompt = (index, newPrompt) => {
    const updated = [...tagPrompts];
    updated[index].prompt = newPrompt;
    setTagPrompts(updated);
  };

  // New handlers for the generative answer specific dropdown
  const handleGenMenuOpen = (event, type, tagIndex = null) => {
    setGenAnchorEl(event.currentTarget);
    setGenMenuType(type);
    setActiveTagIndex(tagIndex);
  };

  const handleGenMenuClose = () => {
    setGenAnchorEl(null);
    setActiveTagIndex(null);
  };

  const handleGenTagSelect = (value) => {
    if (genMenuType === 'tag' && activeTagIndex !== null) {
      const tagToInsert = `{${value}}`;
      updateTagPromptTag(activeTagIndex, tagPrompts[activeTagIndex].tag + tagToInsert);
    } else if (genMenuType === 'context') {
      insertTokenToEditor(value, 'context');
    } else if (genMenuType === 'prompt' && activeTagIndex !== null) {
      insertTokenToEditor(value, 'prompt', activeTagIndex);
    } else if (genMenuType === 'filePath') {
      insertTokenToEditor(value, 'filePath');
    }
    handleGenMenuClose();
  };

  const handleStatusWindowClose = () => {
    handleCloseStatusWindow();
  };

  // Custom save handler that uses the customSaveData
  const handleCustomSave = () => {
    if (actionType === 'generativeanswerfile' && !filePath.trim()) {
      setFilePathError(t('generative_answer.file_path_error_required', 'A file path is required to continue.'));
      return;
    }

    if (filePathError) {
      setFilePathError('');
    }

    handleSave();
  };

  return (
    <ActionSettingsLayout
      title={resolvedActionTitle}
      isEnabled={isEnabled}
      onEnabledChange={setIsEnabled}
      isSaveDisabled={isSaveDisabled}
      onSave={handleCustomSave}
      onForceRun={handleForceRun}
      forceRunLoading={forceRunLoading}
      forceRunResult={forceRunResult}
      popUpOpen={popUpOpen}
      onCloseStatusWindow={handleStatusWindowClose}
      forceRunWarningOpen={forceRunWarningOpen}
      forceRunWarningCount={forceRunWarningCount}
      onForceRunConfirm={handleForceRunConfirm}
      onForceRunCancel={handleForceRunCancel}
      conditions={conditions}
      onConditionsChange={handleConditionsChange}
      tags={tags}
    >
      <Grid container spacing={3}>
        {/* Context Input */}
        <Grid item xs={12}>
          <Card sx={{
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
            border: '1px solid #7b61ff10',
            overflow: 'hidden',
          }}>
            <CardHeader
              title={
                <Typography sx={{
                  fontWeight: 700,
                  color: '#444',
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1
                }}>
                  <Box component="span" sx={{
                    bgcolor: '#7b61ff',
                    color: 'white',
                    borderRadius: '6px',
                    px: { xs: 1, sm: 1.5 },
                    py: { xs: 0.5, sm: 0.7 },
                    mr: { xs: 1, sm: 2 },
                    fontSize: { xs: '0.75rem', sm: '0.85rem' },
                    letterSpacing: '0.5px',
                    fontWeight: 600,
                    boxShadow: '0 3px 6px rgba(123, 97, 255, 0.2)',
                  }}>
                    CONTEXT
                  </Box>
                  {t('generative_answer.context_label', 'Overall Context')}
                </Typography>
              }
              sx={{
                borderBottom: '1px solid #f0f0f8',
                backgroundColor: '#fafbff',
                borderTopLeftRadius: '12px',
                borderTopRightRadius: '12px',
                p: { xs: 1.5, sm: 2.5 },
              }}
            />
            <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
              <TagEditor
                    caseid={caseid}
                id={`gen-context-${settingId}`}
                aria-describedby={`gen-context-help-${settingId}`}
                ref={contextRef}
                value={context}
                onChange={setContext}
                placeholder={t('generative_answer.context_placeholder', 'Describe the overall context for all AI generations...')}
                minHeight="100px"
                onTagClick={(e) => handleGenMenuOpen(e, 'context')}
                tags={tags}
              />

              <Typography
                id={`gen-context-help-${settingId}`}
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 1 }}
              >
                {t('generative_answer.context_help', 'Context the model should always consider (keeps outputs consistent).')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {actionType === 'generativeanswerfile' && (
          <Grid item xs={12}>
            <Card sx={{
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
              border: '1px solid #7b61ff10',
              overflow: 'hidden',
            }}>
              <CardHeader
                title={
                  <Typography sx={{
                    fontWeight: 700,
                    color: '#444',
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1
                  }}>
                    <Box component="span" sx={{
                      bgcolor: '#7b61ff',
                      color: 'white',
                      borderRadius: '6px',
                      px: { xs: 1, sm: 1.5 },
                      py: { xs: 0.5, sm: 0.7 },
                      mr: { xs: 1, sm: 2 },
                      fontSize: { xs: '0.75rem', sm: '0.85rem' },
                      letterSpacing: '0.5px',
                      fontWeight: 600,
                      boxShadow: '0 3px 6px rgba(123, 97, 255, 0.2)',
                    }}>
                      FILE
                    </Box>
                    {t('generative_answer.file_path_label', 'Source File Path')}
                  </Typography>
                }
                sx={{
                  borderBottom: '1px solid #f0f0f8',
                  backgroundColor: '#fafbff',
                  borderTopLeftRadius: '12px',
                  borderTopRightRadius: '12px',
                  p: { xs: 1.5, sm: 2.5 },
                }}
              />
              <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
                <TextField
                  fullWidth
                  size="small"
                  label={t('generative_answer.file_path_label', 'Source File Path')}
                  placeholder={t('generative_answer.file_path_placeholder', 'Enter the file path (tags allowed)')}
                  value={filePath}
                  onChange={(e) => {
                    setFilePath(e.target.value);
                    if (filePathError) {
                      setFilePathError('');
                    }
                  }}
                  error={Boolean(filePathError)}
                  helperText={filePathHelperText}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={t('action_common.insert_tag_tooltip')}>
                          <IconButton
                            onClick={(e) => handleGenMenuOpen(e, 'filePath')}
                            size="small"
                            sx={{
                              color: '#7b61ff',
                              '&:hover': {
                                backgroundColor: 'rgba(123, 97, 255, 0.1)',
                              },
                            }}
                          >
                            <LocalOfferIcon />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#d0d0e8',
                      },
                      '&:hover fieldset': {
                        borderColor: '#7b61ff',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#7b61ff',
                      },
                    },
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<FolderOpenIcon />}
                    onClick={handleFileBrowse}
                    disabled={fileBrowseDisabled}
                    sx={{
                      borderColor: '#7b61ff',
                      color: '#7b61ff',
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: 'rgba(123, 97, 255, 0.08)',
                        borderColor: '#7b61ff',
                      }
                    }}
                  >
                    {t('common.browse', 'Browse')}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Tag Prompts Section */}
        <Grid item xs={12}>
          <Card sx={{
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
            border: '1px solid #7b61ff10',
            overflow: 'hidden',
          }}>
            <CardHeader
              title={
                <Typography sx={{
                  fontWeight: 700,
                  color: '#444',
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1
                }}>
                  <Box component="span" sx={{
                    bgcolor: '#7b61ff',
                    color: 'white',
                    borderRadius: '6px',
                    px: { xs: 1, sm: 1.5 },
                    py: { xs: 0.5, sm: 0.7 },
                    mr: { xs: 1, sm: 2 },
                    fontSize: { xs: '0.75rem', sm: '0.85rem' },
                    letterSpacing: '0.5px',
                    fontWeight: 600,
                    boxShadow: '0 3px 6px rgba(123, 97, 255, 0.2)',
                  }}>
                    GENERATE
                  </Box>
                  {t('generative_answer.tag_prompts_label', 'Tag-Specific Prompts')}
                </Typography>
              }
              action={
                <Button
                  onClick={addTagPrompt}
                  startIcon={<AddIcon />}
                  sx={{
                    color: '#7b61ff',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: '#f8f7ff',
                    },
                  }}
                >
                  {t('generative_answer.add_tag_prompt', 'Add Tag')}
                </Button>
              }
              sx={{
                borderBottom: '1px solid #f0f0f8',
                backgroundColor: '#fafbff',
                borderTopLeftRadius: '12px',
                borderTopRightRadius: '12px',
                p: { xs: 1.5, sm: 2.5 },
              }}
            />
            <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
              {tagPrompts.map((tagPrompt, index) => (
                <Card key={tagPrompt.id} sx={{
                  mb: 2,
                  border: '1px solid #e0e0f0',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  '&:last-child': { mb: 0 }
                }}>
                  <Box sx={{
                    p: 2,
                    backgroundColor: '#f8f9fc',
                    borderBottom: '1px solid #e0e0f0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2
                  }}>
                    <Typography variant="subtitle2" sx={{
                      color: '#7b61ff',
                      fontWeight: 600,
                      minWidth: '60px'
                    }}>
                      Tag {index + 1}:
                    </Typography>
                    <TextField
                      id={`gen-tag-${tagPrompt.id}`}
                      aria-describedby={`gen-tag-help-${tagPrompt.id}`}
                      fullWidth
                      size="small"
                      value={tagPrompt.tag}
                      onChange={(e) => updateTagPromptTag(index, e.target.value)}
                      variant="outlined"
                      placeholder={t('generative_answer.tag_placeholder', 'Enter tag name...')}
                      helperText={t('generative_answer.tag_name_help', 'Tag to save the AI output (e.g. case_summary).')}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title={t('action_common.insert_tag_tooltip')}>
                              <IconButton 
                                onClick={(e) => handleGenMenuOpen(e, 'tag', index)} 
                                size="small"
                                sx={{
                                  color: '#7b61ff',
                                  '&:hover': {
                                    backgroundColor: 'rgba(123, 97, 255, 0.1)',
                                  },
                                }}
                              >
                                <LocalOfferIcon />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: '#d0d0e8',
                          },
                          '&:hover fieldset': {
                            borderColor: '#7b61ff',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#7b61ff',
                          },
                        },
                      }}
                    />
                    {tagPrompts.length > 1 && (
                      <Tooltip title={t('generative_answer.remove_tag_prompt', 'Remove this tag')}>
                        <IconButton
                          onClick={() => removeTagPrompt(index)}
                          size="small"
                          sx={{
                            color: '#e53e3e',
                            '&:hover': {
                              backgroundColor: 'rgba(229, 62, 62, 0.1)',
                            },
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <Box sx={{ p: 2 }}>
                    <Typography variant="subtitle2" sx={{
                      mb: 1,
                      color: '#666',
                      fontWeight: 600
                    }}>
                      Prompt:
                    </Typography>
                    <TagEditor
                    caseid={caseid}
                      id={`gen-prompt-${tagPrompt.id}`}
                      aria-describedby={`gen-prompt-help-${tagPrompt.id}`}
                      ref={el => promptRefs.current[tagPrompt.id] = el}
                      value={tagPrompt.prompt}
                      onChange={(newVal) => updateTagPromptPrompt(index, newVal)}
                      placeholder={t('generative_answer.prompt_placeholder', `Enter specific prompt for ${tagPrompt.tag || 'this tag'}...`)}
                      minHeight="100px"
                      onTagClick={(e) => handleGenMenuOpen(e, 'prompt', index)}
                      tags={tags}
                    />

                    <Typography
                      id={`gen-prompt-help-${tagPrompt.id}`}
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 1 }}
                    >
                      {t('generative_answer.prompt_help', 'Prompt for this tag — specify format, length or tone; supports {tags}.')}
                    </Typography>
                  </Box>
                </Card>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <TagsDropdown
        tags={tags}
        anchorEl={genAnchorEl}
        handleTagSelect={handleGenTagSelect}
        setAnchorEl={handleGenMenuClose}
        menuType={genMenuType}
      />
      {actionType === 'generativeanswerfile' && FileBrowserComponent}
    </ActionSettingsLayout>
  );
};

export default ActionsGenerativeAnswer;
